// Helpers to display the values of an actor change in a readable way.
//
// The audit trail stores the values as they are tracked by the provenance: plain properties
// as text, nested properties (taxonomies, addresses, funding rounds, social handles, ...) as
// json. Showing that json as is makes the overview unreadable, so it is formatted here.
//
// This is a display concern only. A revert never uses the formatted value: the frontend sends
// the entry id and the property names, the backend reads the original value from the database.

import { diffParts } from './dmp.js'
import escapeHTML from './escapeHTML.js'

// Keys that carry no meaning for a reader
const TECHNICAL_KEYS = [
  'actor_id',
  'created_at',
  'ecosystem_id',
  'elastic_id',
  'hidden',
  'id',
  'team_id',
  'updated_at',
  'uuid',
  'value_hash',
]

// Keys that hold the readable representation of an object, in order of preference
const LABEL_KEYS = [
  'name',
  'label',
  'title',
  'text',
  'value',
  'handle',
  'url',
  'email',
  'full_name',
]

const EMPTY = '—'

// Objects can be nested a few levels deep, stop before the output becomes unreadable
const MAX_DEPTH = 3

/**
 * Turn a stored change value into readable text
 *
 * @param {string|null} value The value as it is stored in the audit trail
 * @returns {string}
 */
export function humanizeChangeValue (value) {
  if (value === null || value === undefined || value === '' || value === 'null') {
    return EMPTY
  }

  if (typeof value !== 'string') {
    return format(value, 0)
  }

  const trimmed = value.trim()

  // Only json objects and arrays are parsed, a plain value is shown as it is
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value
  }

  try {
    return format(JSON.parse(trimmed), 0)
  } catch (error) {
    // Not valid json after all, show the raw value instead of an error
    return value
  }
}

// Values are stored in full, so this is only a safety net for a pathological value.
// Everything below it is highlighted, a long description included. The diff library
// has its own one second time limit, so a big value cannot block the page either.
const MAX_DIFF_LENGTH = 100000

/**
 * Build the two sides of a change with the differences highlighted
 *
 * The previous value keeps the parts that were removed, the new value keeps the parts
 * that were added, so a reader sees at a glance which part of the value changed.
 *
 * @param {string|null} from The previous value as stored in the audit trail
 * @param {string|null} to The new value as stored in the audit trail
 * @returns {{before: string, after: string, isEmptyBefore: boolean, isEmptyAfter: boolean}}
 *          html safe strings, the differences wrapped in del and ins elements
 */
export function highlightChange (from, to) {
  const before = humanizeChangeValue(from)
  const after = humanizeChangeValue(to)

  const result = {
    before: escapeHTML(before),
    after: escapeHTML(after),
    isEmptyBefore: before === EMPTY,
    isEmptyAfter: after === EMPTY,
  }

  // Nothing to compare when one of both sides is empty or when the values are too long
  if (result.isEmptyBefore || result.isEmptyAfter ||
    before.length > MAX_DIFF_LENGTH || after.length > MAX_DIFF_LENGTH) {
    return result
  }

  const parts = diffParts(before, after)

  result.before = parts
    .filter(([operation]) => operation <= 0)
    .map(([operation, text]) => operation === 0 ? escapeHTML(text) : '<del>' + escapeHTML(text) + '</del>')
    .join('')

  result.after = parts
    .filter(([operation]) => operation >= 0)
    .map(([operation, text]) => operation === 0 ? escapeHTML(text) : '<ins>' + escapeHTML(text) + '</ins>')
    .join('')

  return result
}

/**
 * Turn a property name into a readable label, e.g. activities_description
 * becomes "Activities description"
 *
 * @param {string} property
 * @returns {string}
 */
export function humanizeProperty (property) {
  if (!property) {
    return ''
  }

  const label = String(property).replace(/_/g, ' ').trim()

  return label.charAt(0).toUpperCase() + label.slice(1)
}

function format (value, depth) {
  if (value === null || value === undefined || value === '') {
    return EMPTY
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (typeof value === 'number') {
    return String(value)
  }

  if (typeof value === 'string') {
    return value
  }

  if (depth >= MAX_DEPTH) {
    return JSON.stringify(value)
  }

  if (Array.isArray(value)) {
    const parts = value
      .map(entry => format(entry, depth + 1))
      .filter(part => part !== EMPTY && part !== '')

    return parts.length ? parts.join(', ') : EMPTY
  }

  return formatObject(value, depth)
}

function formatObject (value, depth) {
  // An object that carries a readable label is shown by that label alone
  for (const key of LABEL_KEYS) {
    if (typeof value[key] === 'string' && value[key] !== '') {
      return value[key]
    }
  }

  const parts = []

  for (const [key, nested] of Object.entries(value)) {
    if (TECHNICAL_KEYS.includes(key)) {
      continue
    }

    const formatted = format(nested, depth + 1)

    if (formatted === EMPTY || formatted === '') {
      continue
    }

    parts.push(humanizeProperty(key).toLowerCase() + ': ' + formatted)
  }

  return parts.length ? parts.join(', ') : EMPTY
}
